import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { apiClient, setAccessToken, extractErrorMessage } from '@/api/client';
import type { CurrentUser } from '@/types/domain';

export const useAuthStore = defineStore('auth', () => {
  const user = ref<CurrentUser | null>(null);
  const isReady = ref(false);
  const isAuthenticated = computed(() => !!user.value);

  function hasPermission(permission: string): boolean {
    return user.value?.permissions.includes(permission) ?? false;
  }

  function hasAnyRole(...roles: string[]): boolean {
    return roles.some((role) => user.value?.roles.includes(role));
  }

  /**
   * Primer paso del login. Si la cuenta tiene verificacion en dos pasos,
   * devuelve el reto y el login se completa con verifyTwoFactor().
   */
  async function login(email: string, password: string): Promise<{ challengeToken: string } | null> {
    const { data } = await apiClient.post('/auth/login', { email, password });
    if (data.twoFactorRequired) return { challengeToken: data.challengeToken as string };
    setAccessToken(data.accessToken);
    user.value = data.user;
    return null;
  }

  async function verifyTwoFactor(challengeToken: string, code: string) {
    const { data } = await apiClient.post('/auth/2fa/verify', { challengeToken, code });
    setAccessToken(data.accessToken);
    user.value = data.user;
  }

  function setAvatarUpdatedAt(value: string | null) {
    if (user.value) user.value = { ...user.value, avatarUpdatedAt: value };
  }

  function setTwoFactorEnabled(enabled: boolean) {
    if (user.value) user.value = { ...user.value, twoFactorEnabled: enabled };
  }

  async function logout() {
    try {
      await apiClient.post('/auth/logout');
    } finally {
      setAccessToken(null);
      user.value = null;
    }
  }

  async function tryRestoreSession() {
    try {
      const { data } = await apiClient.post('/auth/refresh');
      setAccessToken(data.accessToken);
      user.value = data.user;
    } catch {
      setAccessToken(null);
      user.value = null;
    } finally {
      isReady.value = true;
    }
  }

  return {
    user,
    isReady,
    isAuthenticated,
    hasPermission,
    hasAnyRole,
    login,
    verifyTwoFactor,
    setTwoFactorEnabled,
    setAvatarUpdatedAt,
    logout,
    tryRestoreSession,
    extractErrorMessage,
  };
});
